import React, { useEffect, useState } from 'react';
import { 
  Grid, 
  Typography, 
  Button, 
  Card, 
  CardContent, 
  CardMedia, 
  Box, 
  Snackbar, 
  Modal, 
  CircularProgress,
  Tooltip,
  Divider,
  IconButton
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useAuth } from '../AuthContext';
import useAuthenticatedFetch from '../hooks/useAuthenticatedFetch';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from '../components/dialogs/ConfirmDialog';

const AdsManager = ({ onIsDirtyChange }) => {
  const { user } = useAuth();
  const [ads, setAds] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [preview, setPreview] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const fetchWithAuth = useAuthenticatedFetch();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [zoomImage, setZoomImage] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [proActive, setProActive] = useState(null);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({ open: false, adId: null });

  const FREE_MAX_ADS = 1;
  const atFreeAdLimit = proActive === false && ads.length >= FREE_MAX_ADS;

  useEffect(() => {
    if (onIsDirtyChange) {
      onIsDirtyChange(isDirty);
    }
  }, [isDirty, onIsDirtyChange]);

  useEffect(() => {
    fetchAds();
  }, [user.communityId]);

  useEffect(() => {
    let cancelled = false;
    const loadBilling = async () => {
      if (!user?.communityId) {
        setProActive(false);
        return;
      }
      try {
        const res = await fetchWithAuth(
          `${process.env.REACT_APP_BASE_URL}/billing/status/${user.communityId}`,
        );
        const json = await res.json().catch(() => null);
        if (!cancelled) setProActive(Boolean(json?.proActive));
      } catch {
        if (!cancelled) setProActive(false);
      }
    };
    loadBilling();
    return () => {
      cancelled = true;
    };
  }, [fetchWithAuth, user?.communityId]);

  const fetchAds = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/ad/getAds/${user.communityId}`);
      const data = await response.json();
      if (data.length === 0) {
        setSuccessMessage(t('ads.noAdsMessage'));
      } else {
        setAds(data);
      }
    } catch (err) {
      console.error(t('ads.fetchError'), err);
      setErrorMessage(t('ads.fetchError'));
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) {
      setErrorMessage(t('ads.noFileSelected'));
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setErrorMessage(t('ads.fileTooLarge'));
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }

    const allowedTypes = ["image/jpeg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      setErrorMessage(t('ads.invalidFileType'));
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }

    setErrorMessage("");
    setIsUploading(true);

    const previewUrl = URL.createObjectURL(file);
    setPreview(previewUrl);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('communityId', user.communityId);

    try {
      const res = await fetchWithAuth(`${process.env.REACT_APP_BASE_URL}/ad/uploadAd`, {
        method: 'POST',
        body: formData,
      });

      const data = await res.json();
      setSuccessMessage(t('ads.adAddedSuccess'));
      setTimeout(() => setSuccessMessage(''), 3000);

      setAds((prevAds) => [
        ...prevAds,
        {
          name: file.name,
          filePath: data.filePath,
          id: data.adId,
          isSelected: false
        },
      ]);
      setPreview(null);
      setIsDirty(true);

    } catch (err) {
      if (err?.code === 'PLAN_LIMIT') {
        setErrorMessage(t('ads.planLimitFree', { max: FREE_MAX_ADS }));
      } else if (err?.code === 'FEATURE_PRO_ONLY') {
        setErrorMessage(t('ads.proOnly'));
      } else {
        setErrorMessage(t('ads.addError'));
      }
      setTimeout(() => setErrorMessage(''), 5000);
      console.error(err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleToggleAd = async (adId) => {
    try {
      const updatedAds = ads.map(ad => 
        ad.id === adId ? { ...ad, isSelected: !ad.isSelected } : ad
      );
      
      const selectedAds = updatedAds.filter(ad => ad.isSelected).map(ad => ad.id);
      
      const response = await fetchWithAuth(`${process.env.REACT_APP_BASE_URL}/ad/selectAds`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          communityId: user.communityId, 
          adIds: selectedAds 
        }),
      });

      if (!response.ok) throw new Error(t('ads.activateError'));

      setAds(updatedAds);
      setSuccessMessage(t('ads.adsActivated'));
      setTimeout(() => setSuccessMessage(''), 3000);
      setIsDirty(false);
    } catch (error) {
      console.error(error);
      setErrorMessage(t('ads.activateError'));
    }
  };

  const handleDeleteAd = (adId) => {
    setDeleteConfirmDialog({ open: true, adId });
  };

  const handleConfirmDelete = async () => {
    try {
      const response = await fetchWithAuth(`${process.env.REACT_APP_BASE_URL}/ad/deleteAds`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          adIds: [deleteConfirmDialog.adId], 
          communityId: user.communityId 
        }),
      });

      if (!response.ok) throw new Error(t('ads.deleteError'));

      setAds(ads.filter(ad => ad.id !== deleteConfirmDialog.adId));
      setSuccessMessage(t('ads.adsDeleted'));
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error(error);
      setErrorMessage(t('ads.deleteError'));
    } finally {
      setDeleteConfirmDialog({ open: false, adId: null });
    }
  };

  const handleClose = () => setOpen(false);
  const handleImageClick = (imagePath) => {
    setZoomImage(imagePath);
    setOpen(true);
  };

  const activeAds = ads.filter(ad => ad.isSelected);
  const inactiveAds = ads.filter(ad => !ad.isSelected);

  return (
    <Box>
      <Typography variant="h5" gutterBottom>{t('ads.title')}</Typography>
      
      <Box sx={{ mb: 3 }}>
        <Button 
          component="label" 
          variant="contained" 
          startIcon={isUploading ? <CircularProgress size={20} color="inherit" /> : <UploadFileIcon />}
          disabled={isUploading || atFreeAdLimit}
        >
          {isUploading ? t('ads.uploading') : t('ads.addAd')}
          <input type="file" hidden onChange={handleFileUpload} disabled={isUploading || atFreeAdLimit} />
        </Button>
        {atFreeAdLimit ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('ads.planLimitFree', { max: FREE_MAX_ADS })}
          </Typography>
        ) : null}
      </Box>

      {preview && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2">{t('ads.preview')}</Typography>
          <img src={preview} alt={t('ads.previewAlt')} style={{ maxWidth: '100%', maxHeight: '200px' }} />
        </Box>
      )}

      {activeAds.length > 0 && (
        <>
          <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>{t('ads.activeAds')}</Typography>
          <Grid container spacing={2}>
            {activeAds.map((ad) => (
              <Grid item xs={12} sm={6} md={4} key={ad.id}>
                <Card sx={{ 
                  position: 'relative', 
                  height: '100%',
                  maxWidth: '330px',
                  margin: '0 auto'
                }}>
                  <CardMedia
                    component="img"
                    alt={ad.name}
                    image={ad.filePath}
                    onClick={() => handleImageClick(ad.filePath)}
                    sx={{
                      width: "100%",
                      aspectRatio: "1 / 1.41",
                      objectFit: "cover",
                      cursor: 'pointer',
                      '&:hover': { transform: 'scale(1.05)' },
                      transition: 'transform 0.3s ease',
                    }}
                  />
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      '& .MuiIconButton-root': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.08)',
                        },
                        transition: 'all 0.2s ease-in-out',
                        padding: '8px',
                        '& .MuiSvgIcon-root': {
                          fontSize: '1.5rem'
                        }
                      }
                    }}>
                      <Tooltip title={t('ads.deactivate')}>
                        <IconButton 
                          onClick={() => handleToggleAd(ad.id)} 
                          color="primary"
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('ads.delete')}>
                        <IconButton 
                          onClick={() => handleDeleteAd(ad.id)} 
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {inactiveAds.length > 0 && (
        <>
          <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>{t('ads.inactiveAds')}</Typography>
          <Grid container spacing={2}>
            {inactiveAds.map((ad) => (
              <Grid item xs={12} sm={6} md={4} key={ad.id}>
                <Card sx={{ 
                  position: 'relative', 
                  height: '100%',
                  maxWidth: '330px',
                  margin: '0 auto',
                  opacity: 0.7 
                }}>
                  <CardMedia
                    component="img"
                    alt={ad.name}
                    image={ad.filePath}
                    onClick={() => handleImageClick(ad.filePath)}
                    sx={{
                      width: "100%",
                      aspectRatio: "1 / 1.41",
                      objectFit: "cover",
                      cursor: 'pointer',
                      '&:hover': { transform: 'scale(1.05)' },
                      transition: 'transform 0.3s ease',
                    }}
                  />
                  <CardContent sx={{ paddingTop: '10px' }}>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      '& .MuiIconButton-root': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.08)',
                        },
                        transition: 'all 0.2s ease-in-out',
                        padding: '8px',
                        '& .MuiSvgIcon-root': {
                          fontSize: '1.5rem'
                        }
                      }
                    }}>
                      <Tooltip title={t('ads.activate')}>
                        <IconButton 
                          onClick={() => handleToggleAd(ad.id)} 
                          color="primary"
                        >
                          <VisibilityOffIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('ads.delete')}>
                        <IconButton 
                          onClick={() => handleDeleteAd(ad.id)} 
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      <Modal open={open} onClose={handleClose} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box
          component="img"
          src={zoomImage}
          alt="Zoomed template"
          sx={{ maxWidth: '90%', maxHeight: '90%', border: '5px solid white', boxShadow: 24 }}
        />
      </Modal>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage('')}
        message={successMessage}
      />
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={3000}
        onClose={() => setErrorMessage('')}
        message={errorMessage}
        severity="error"
      />

      <ConfirmDialog
        open={deleteConfirmDialog.open}
        title={t('ads.confirmDelete')}
        message={t('ads.confirmDeleteMessage')}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteConfirmDialog({ open: false, adId: null })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        severity="error"
      />
    </Box>
  );
};

export default AdsManager;
